'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useWbsStore } from '@/stores/wbsStore';
import { useAuthStore } from '@/stores/authStore';
import { categoriesApi, tasksApi } from '@/lib/api';
import type { StageArtifact } from '@/lib/api';
import type { Task } from '@/types';

interface TaskTableProps {
  onEditTask: (task: Task) => void;
  onAddSubTask?: (parentTask: Task) => void;
}

// 카테고리 색상 캐시
let stageColorCache: Record<string, string> = {};
let statusColorCache: Record<string, string> = {};

export default function TaskTable({ onEditTask, onAddSubTask }: TaskTableProps) {
  const { tasks, selectedTasks, toggleTaskSelection, selectAllTasks, clearSelection, reorderTasks } = useWbsStore();
  const { isViewer } = useAuthStore();
  const [stageColors, setStageColors] = useState<Record<string, string>>(stageColorCache);
  const [statusColors, setStatusColors] = useState<Record<string, string>>(statusColorCache);
  const [dragOverId, setDragOverId] = useState<number | null>(null);
  const [dragOverPosition, setDragOverPosition] = useState<'above' | 'below' | null>(null);
  const dragItemRef = useRef<Task | null>(null);

  // 단계별 산출물 현황 (작업중 태그 표시용)
  const [stageArtifactsMap, setStageArtifactsMap] = useState<Record<string, StageArtifact[]>>({});

  useEffect(() => {
    const stages = [...new Set(tasks.filter(t => t.depth === 2).map(t => t.stage_name))];
    if (stages.length === 0) return;

    const loadArtifacts = async () => {
      const result: Record<string, StageArtifact[]> = {};
      await Promise.all(
        stages.map(async (stage) => {
          try {
            const data = await tasksApi.getStageArtifacts(stage);
            // 작업중인 산출물만 (total > 0인 것)
            const active = data.artifacts.filter(a => a.total > 0);
            if (active.length > 0) result[stage] = active;
          } catch { /* skip */ }
        })
      );
      setStageArtifactsMap(result);
    };

    loadArtifacts();
  }, [tasks]);

  // 카테고리에서 색상 로드
  useEffect(() => {
    if (Object.keys(stageColorCache).length > 0) return;

    const loadColors = async () => {
      try {
        const [stageItems, statusItems] = await Promise.all([
          categoriesApi.getItems('task_stage'),
          categoriesApi.getItems('task_status'),
        ]);

        const sColors: Record<string, string> = {};
        stageItems.forEach(item => {
          if (item.color) sColors[item.code] = item.color;
        });

        const tColors: Record<string, string> = {};
        statusItems.forEach(item => {
          if (item.color) tColors[item.code] = item.color;
        });

        stageColorCache = sColors;
        statusColorCache = tColors;
        setStageColors(sColors);
        setStatusColors(tColors);
      } catch (error) {
        console.error('Failed to load category colors:', error);
      }
    };

    loadColors();
  }, []);

  const sortedTasks = [...tasks].sort((a, b) => a.order_index - b.order_index);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      selectAllTasks(sortedTasks.map(t => t.id));
    } else {
      clearSelection();
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  // 드래그앤드롭 핸들러
  const handleDragStart = useCallback((e: React.DragEvent, task: Task) => {
    dragItemRef.current = task;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(task.id));
    // 투명도 효과
    const target = e.currentTarget as HTMLElement;
    setTimeout(() => target.style.opacity = '0.4', 0);
  }, []);

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.style.opacity = '1';
    setDragOverId(null);
    setDragOverPosition(null);
    dragItemRef.current = null;
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, task: Task) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const position = e.clientY < midY ? 'above' : 'below';

    setDragOverId(task.id);
    setDragOverPosition(position);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverId(null);
    setDragOverPosition(null);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent, targetTask: Task) => {
    e.preventDefault();
    setDragOverId(null);
    setDragOverPosition(null);

    const dragTask = dragItemRef.current;
    if (!dragTask || dragTask.id === targetTask.id) return;

    // 새로운 순서 계산
    const targetIdx = sortedTasks.findIndex(t => t.id === targetTask.id);
    if (targetIdx === -1) return;

    // 드래그한 항목을 제거한 목록
    const withoutDrag = sortedTasks.filter(t => t.id !== dragTask.id);
    const newTargetIdx = withoutDrag.findIndex(t => t.id === targetTask.id);

    // 삽입 위치 결정
    const insertIdx = dragOverPosition === 'above' ? newTargetIdx : newTargetIdx + 1;
    withoutDrag.splice(insertIdx, 0, dragTask);

    // order_index 재계산
    const reorderItems = withoutDrag.map((task, idx) => ({
      id: task.id,
      order_index: (idx + 1) * 1.0,
    }));

    try {
      await reorderTasks(reorderItems);
    } catch (error) {
      console.error('Reorder failed:', error);
    }
  }, [sortedTasks, dragOverPosition, reorderTasks]);

  if (sortedTasks.length === 0) {
    return (
      <div className="p-12 text-center text-gray-500">
        등록된 작업이 없습니다.
      </div>
    );
  }

  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-100 sticky top-0 z-10 border-b border-gray-200">
        <tr style={{ height: '80px' }}>
          {!isViewer() && (
            <th className="w-12 px-3 py-2 text-center border-r border-gray-200">
              <input
                type="checkbox"
                onChange={handleSelectAll}
                checked={selectedTasks.size === sortedTasks.length && sortedTasks.length > 0}
                className="w-4 h-4 rounded border-gray-300 text-[#1e40af] focus:ring-[#1e40af]"
              />
            </th>
          )}
          <th className="w-28 px-3 py-2 text-center font-semibold text-gray-700 border-r border-gray-200">단계</th>
          <th className="min-w-[200px] px-4 py-2 text-left font-semibold text-gray-700 border-r border-gray-200">작업명</th>
          <th className="w-24 px-3 py-2 text-center font-semibold text-gray-700 border-r border-gray-200">담당자</th>
          <th className="w-28 px-3 py-2 text-center font-semibold text-gray-700 border-r border-gray-200">시작일</th>
          <th className="w-28 px-3 py-2 text-center font-semibold text-gray-700 border-r border-gray-200">종료일</th>
          <th className="w-24 px-3 py-2 text-center font-semibold text-gray-700 border-r border-gray-200">상태</th>
          <th className="w-20 px-3 py-2 text-center font-semibold text-gray-700">공정율</th>
        </tr>
      </thead>
      <tbody>
        {sortedTasks.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            isSelected={selectedTasks.has(task.id)}
            onSelect={() => toggleTaskSelection(task.id)}
            onEdit={() => onEditTask(task)}
            onAddSubTask={onAddSubTask ? () => onAddSubTask(task) : undefined}
            isViewer={isViewer()}
            formatDate={formatDate}
            stageColors={stageColors}
            statusColors={statusColors}
            stageArtifacts={task.depth === 2 ? stageArtifactsMap[task.stage_name] : undefined}
            isDragOver={dragOverId === task.id}
            dragOverPosition={dragOverId === task.id ? dragOverPosition : null}
            onDragStart={(e) => handleDragStart(e, task)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, task)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, task)}
          />
        ))}
      </tbody>
    </table>
  );
}

interface TaskRowProps {
  task: Task;
  isSelected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onAddSubTask?: () => void;
  isViewer: boolean;
  formatDate: (date: string) => string;
  stageColors: Record<string, string>;
  statusColors: Record<string, string>;
  stageArtifacts?: StageArtifact[];
  isDragOver: boolean;
  dragOverPosition: 'above' | 'below' | null;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
}

function hexToBadgeStyle(hex: string | undefined) {
  if (!hex) return {};
  return {
    backgroundColor: hex + '20',
    color: hex,
  };
}

function TaskRow({
  task, isSelected, onSelect, onEdit, onAddSubTask, isViewer,
  formatDate, stageColors, statusColors, stageArtifacts,
  isDragOver, dragOverPosition,
  onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop
}: TaskRowProps) {
  const stageHex = stageColors[task.stage_name];
  const statusHex = statusColors[task.status];

  const depthIndicator = task.depth === 2
    ? <span className="text-gray-400 mr-2">├</span>
    : <span className="text-gray-300 mr-2 pl-4">└</span>;

  const depthBadge = task.depth === 2
    ? <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 mr-2">2</span>
    : <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700 mr-2">3</span>;

  const depthClass = task.depth === 2 ? 'pl-4' : 'pl-8';

  // 드래그 오버 표시 스타일
  const dragIndicatorClass = isDragOver
    ? dragOverPosition === 'above'
      ? 'border-t-2 border-t-blue-500'
      : 'border-b-2 border-b-blue-500'
    : '';

  return (
    <tr
      draggable={!isViewer}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={onEdit}
      className={`border-b border-gray-100 hover:bg-[#f0f9ff] cursor-pointer transition ${dragIndicatorClass} ${!isViewer ? 'cursor-grab active:cursor-grabbing' : ''}`}
      style={{ height: '48px' }}
    >
      {!isViewer && (
        <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onSelect}
            className="w-4 h-4 rounded border-gray-300 text-[#1e40af] focus:ring-[#1e40af]"
          />
        </td>
      )}
      <td className="px-3 py-2 text-center">
        <span
          className="inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold"
          style={stageHex ? hexToBadgeStyle(stageHex) : undefined}
        >
          {task.stage_name}
        </span>
      </td>
      <td className="px-4 py-2">
        <div className={`flex items-center ${depthClass} group`}>
          {depthIndicator}
          {depthBadge}
          <span className="text-gray-700 truncate flex-1" title={task.task_name}>
            {task.task_name}
          </span>
          {/* 산출물 태그 (2뎁스에서 산출물이 있을 때) */}
          {stageArtifacts && stageArtifacts.length > 0 && (
            <div className="flex items-center gap-1 ml-2 flex-shrink-0">
              {stageArtifacts.map((a) => (
                <span
                  key={a.key}
                  className="relative inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium"
                  style={{
                    backgroundColor: a.progress === 100 ? '#f0fdf4' : '#eff6ff',
                    color: a.progress === 100 ? '#166534' : '#1e40af',
                  }}
                  title={`${a.label}: ${a.done}/${a.total} 완료`}
                >
                  {a.has_alert && (
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0" />
                  )}
                  {a.label} {a.done}/{a.total}
                </span>
              ))}
            </div>
          )}
          {/* 하위 작업 추가 버튼 (2뎁스에서만 표시) */}
          {!isViewer && task.depth === 2 && onAddSubTask && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAddSubTask();
              }}
              className="opacity-0 group-hover:opacity-100 ml-2 p-1 rounded hover:bg-blue-100 text-blue-600 transition-opacity flex-shrink-0"
              title="하위 작업 추가"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-center text-gray-600">
        {task.manager || '-'}
      </td>
      <td className="px-3 py-2 text-center text-gray-600">
        {task.start_date ? formatDate(task.start_date) : '-'}
      </td>
      <td className="px-3 py-2 text-center text-gray-600">
        {task.end_date ? formatDate(task.end_date) : '-'}
      </td>
      <td className="px-3 py-2 text-center">
        <span
          className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
          style={statusHex ? hexToBadgeStyle(statusHex) : undefined}
        >
          {task.status}
        </span>
      </td>
      <td className="px-3 py-2 text-center">
        <span className={`font-medium ${task.progress === 100 ? 'text-green-600' : 'text-gray-700'}`}>
          {task.progress}%
        </span>
      </td>
    </tr>
  );
}
